if (!customElements.get('product-modal')) {
  customElements.define('product-modal', class ProductModal extends ModalDialog {
    constructor() {
      super();
      this.initControls();
    }

    connectedCallback() {
      this.initControls();
    }

    initControls() {
      if (this.controlsInitialized) return;

      const dialog = this.querySelector('.product-media-modal__dialog');
      const content = this.querySelector('.product-media-modal__content');
      if (!dialog || !content) return;

      // Stop pointerup bubbling so ModalDialog's pointerup listener doesn't close on content
      content.addEventListener('pointerup', (e) => e.stopPropagation());

      // Ensure navigation bar exists
      let nav = this.querySelector('.product-media-modal__nav');
      if (!nav) {
        nav = document.createElement('div');
        nav.className = 'product-media-modal__nav';
        nav.setAttribute('aria-label', 'Modal gallery controls');
        nav.innerHTML = `
          <button type="button" class="product-media-modal__nav-btn product-media-modal__nav-btn--prev" aria-label="Previous image">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
          <button type="button" class="product-media-modal__nav-btn product-media-modal__nav-btn--close" aria-label="Close modal">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          <button type="button" class="product-media-modal__nav-btn product-media-modal__nav-btn--next" aria-label="Next image">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        `;
        dialog.appendChild(nav);
      }

      nav.addEventListener('pointerup', (e) => e.stopPropagation());

      const prevBtn = nav.querySelector('.product-media-modal__nav-btn--prev');
      const nextBtn = nav.querySelector('.product-media-modal__nav-btn--next');
      const closeBtn = nav.querySelector('.product-media-modal__nav-btn--close');

      if (prevBtn) {
        prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.prevMedia();
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.nextMedia();
        });
      }

      if (closeBtn) {
        closeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.hide();
        });
      }

      // Close when clicking directly on dialog backdrop (not on image or nav)
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog || e.target === content) {
          this.hide();
        }
      });

      // Keyboard navigation
      document.addEventListener('keydown', (e) => {
        if (!this.hasAttribute('open')) return;
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.nextMedia();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.prevMedia();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          this.hide();
        }
      });

      // Touch swipe support on mobile
      let touchStartX = 0;
      let touchStartY = 0;
      content.addEventListener('touchstart', (e) => {
        if (e.touches && e.touches[0]) {
          touchStartX = e.touches[0].clientX;
          touchStartY = e.touches[0].clientY;
        }
      }, { passive: true });

      content.addEventListener('touchend', (e) => {
        if (e.changedTouches && e.changedTouches[0]) {
          const diffX = e.changedTouches[0].clientX - touchStartX;
          const diffY = e.changedTouches[0].clientY - touchStartY;
          if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 40) {
            if (diffX < 0) {
              this.nextMedia();
            } else {
              this.prevMedia();
            }
          }
        }
      }, { passive: true });

      this.controlsInitialized = true;
    }

    getMediaList() {
      return Array.from(this.querySelectorAll('.product-media-modal__content > *')).filter(el => {
        return el.hasAttribute('data-media-id') || el.querySelector('[data-media-id]') || el.tagName === 'IMG' || el.tagName === 'DEFERRED-MEDIA';
      });
    }

    show(opener) {
      super.show(opener);
      this.initControls();
      this.showActiveMedia();
    }

    hide() {
      super.hide();
      this.querySelectorAll('video').forEach(v => v.pause());
    }

    showActiveMedia() {
      const allMedia = this.getMediaList();
      if (!allMedia.length) return;

      let target = null;
      if (this.openedBy) {
        const id = this.openedBy.getAttribute('data-media-id');
        if (id) {
          target = allMedia.find(el => el.getAttribute('data-media-id') === id || el.querySelector(`[data-media-id="${id}"]`));
        }
      }
      if (!target) target = allMedia[0];

      this.setActiveMedia(target);
    }

    setActiveMedia(target) {
      const allMedia = this.getMediaList();
      if (!allMedia.length || !target) return;

      allMedia.forEach(el => {
        el.classList.remove('active');
        const v = el.querySelector('video');
        if (v) v.pause();
      });

      target.classList.add('active');

      if (target.nodeName === 'DEFERRED-MEDIA') {
        const template = target.querySelector('template');
        if (template && template.content.querySelector('.js-youtube')) {
          target.loadContent();
        }
      }

      // Hide navigation arrows if only 1 image
      const prevBtn = this.querySelector('.product-media-modal__nav-btn--prev');
      const nextBtn = this.querySelector('.product-media-modal__nav-btn--next');
      if (prevBtn && nextBtn) {
        const isSingle = allMedia.length <= 1;
        prevBtn.style.display = isSingle ? 'none' : 'flex';
        nextBtn.style.display = isSingle ? 'none' : 'flex';
      }
    }

    nextMedia() {
      const allMedia = this.getMediaList();
      if (allMedia.length <= 1) return;
      const currentIndex = allMedia.findIndex(el => el.classList.contains('active'));
      const nextIndex = (currentIndex === -1 || currentIndex === allMedia.length - 1) ? 0 : currentIndex + 1;
      this.setActiveMedia(allMedia[nextIndex]);
    }

    prevMedia() {
      const allMedia = this.getMediaList();
      if (allMedia.length <= 1) return;
      const currentIndex = allMedia.findIndex(el => el.classList.contains('active'));
      const prevIndex = (currentIndex <= 0) ? allMedia.length - 1 : currentIndex - 1;
      this.setActiveMedia(allMedia[prevIndex]);
    }
  });
}

